import { inject, Injectable } from '@angular/core';
import { UserDashboardResponse } from '@meddev/fe-shared';
import { Store } from '@ngxs/store';
import * as _ from 'lodash';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, startWith } from 'rxjs/operators';
import { AppModulesType } from '../enums/appModulesType.enum';
import { MenuGroup } from '../model/menu-group';
import { MenuItem } from '../model/menu-item';
import { Module } from '../model/module.model';
import { BaseState } from '../state/base/base.state';
import { AnalyticsService } from './analytics.service';

@Injectable({
    providedIn: 'root',
})
export class MenuService {
    private analyticsService = inject(AnalyticsService);
    private store = inject(Store);

    private mainMenu: MenuGroup[] = [
        {
            groupTitle: 'Pregledi',
            menu: [
                {
                    title: 'Seznam naročil',
                    icon: 'las pl-1 la-list',
                    link: '/reservations-list',
                    wip: false,
                },
                {
                    title: 'Mreža izvajalcev',
                    icon: 'las pl-1 la-stethoscope',
                    link: '/contractor-network',
                    wip: false,
                },
            ],
        },

        {
            groupTitle: 'Zaposleni',
            menu: [
                {
                    title: 'Seznam delavcev',
                    icon: 'las pl-1 la-poll-h',
                    link: '/workers',
                },
                {
                    title: 'Delovne skupine',
                    icon: 'las pl-1 la-users',
                    link: '/working-groups',
                    module: AppModulesType.WORK_GROUPS,
                },
            ],
        },
        {
            groupTitle: 'Analitika',
            menu: [],
        },

        {
            groupTitle: 'Nastavitve',
            menu: [
                { title: 'Moje podjetje', icon: 'pl-1  las  la-hospital', link: '/settings/bussines-info' },
                { title: 'Moj račun', icon: 'pl-1  las  la-user', link: '/settings/user-profile', wip: false },
                { title: 'Odjava', icon: 'pl-1  las  la-sign-out-alt', link: '/public/logout' },
            ],
        },
    ];

    /** Emit to force menu to recompute (e.g. after first login when store was updated before menu subscribed). */
    private readonly menuRefresh$ = new Subject<void>();

    public mainMenu$: Observable<MenuGroup[]> = combineLatest([
        this.analyticsService.metabaseAnalyticsDashboards$.pipe(
            catchError(() => {
                return of(null);
            }),
        ),
        this.store.select(BaseState.GetActiveModules),
        this.menuRefresh$.pipe(startWith(undefined)),
    ]).pipe(
        map(([metabaseAnalyticsDashboards, activeModules]) => {
            const mainMenu = _.cloneDeep(this.mainMenu);
            const analyticsGroup = mainMenu.find(item => {
                return item.groupTitle === 'Analitika';
            });
            const dashboards = metabaseAnalyticsDashboards?.groups?.reduce<UserDashboardResponse[]>((acc, g) => acc.concat(g.dashboards), []) ?? [];
            if (analyticsGroup && dashboards.length) {
                analyticsGroup.menu.push(
                    ...dashboards.map((g: UserDashboardResponse) => {
                        return {
                            title: g.label,
                            icon: 'las pl-1 la-poll-h',
                            link: `/analytics`,
                            queryParams: {
                                view: g.uuid,
                            },
                        } as MenuItem;
                    }),
                );
            }
            return this.validateMenu(mainMenu, activeModules ?? []);
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    /** Call after login (or when active modules may have been set after the menu already subscribed). */
    public refreshMenu(): void {
        this.menuRefresh$.next();
    }

    private validateMenu(menuGroup: MenuGroup[], activeModules: Module[]): MenuGroup[] {
        const enabledModuleNames = activeModules?.map((el: Module) => el.name) ?? [];
        return menuGroup.filter(menuG => {
            menuG.menu = menuG.menu.filter(item => {
                if (item.module != undefined) {
                    return enabledModuleNames.includes(item.module);
                }
                return true;
            });
            return menuG?.menu?.length > 0;
        });
    }
}
